import React, { useState } from 'react';
import { Objective } from '../lib/supabase';
import './Objectives.css';

interface ObjectivesProps {
  objectives: Objective[];
  onAddObjective: (objective: Omit<Objective, 'id' | 'created_at' | 'updated_at'>) => void;
  onUpdateObjective: (id: string, updates: Partial<Objective>) => void;
  onDeleteObjective: (id: string) => void;
}

const Objectives: React.FC<ObjectivesProps> = ({
  objectives,
  onAddObjective,
  onUpdateObjective,
  onDeleteObjective,
}) => {
  const [isAdding, setIsAdding] = useState(false);
  const [newContent, setNewContent] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [editCategory, setEditCategory] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newContent.trim()) return;
    
    onAddObjective({
      content: newContent.trim(),
      category: newCategory.trim() || undefined,
    });
    
    setNewContent('');
    setNewCategory('');
    setIsAdding(false);
  };

  const handleStartEdit = (obj: Objective) => {
    setEditingId(obj.id || null);
    setEditContent(obj.content);
    setEditCategory(obj.category || '');
  };

  const handleSaveEdit = (id: string) => {
    onUpdateObjective(id, {
      content: editContent.trim(),
      category: editCategory.trim() || undefined,
    });
    setEditingId(null);
    setEditContent('');
    setEditCategory('');
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditContent('');
    setEditCategory('');
  };

  return (
    <div className="objectives">
      <div className="objectives-header">
        <h3>2026 Company Objectives</h3>
        {!isAdding && (
          <button className="btn-add-event" onClick={() => setIsAdding(true)}>
            + Add Objective
          </button>
        )}
      </div>

      {isAdding && (
        <div className="card">
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Objective</label>
              <input
                type="text"
                value={newContent}
                onChange={(e) => setNewContent(e.target.value)}
                placeholder="e.g., Launch PLAi Ground"
                required
                autoFocus
              />
            </div>
            <div className="form-group">
              <label>Category (optional)</label>
              <input
                type="text"
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                placeholder="e.g., Product, Partnerships, Events"
              />
            </div>
            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={() => {
                setIsAdding(false);
                setNewContent('');
                setNewCategory('');
              }}>
                Cancel
              </button>
              <button type="submit" className="btn-add-event">
                Add Objective
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="objectives-list">
        {objectives.length === 0 ? (
          <div className="card">
            <p className="muted">No objectives yet. Add your first objective above.</p>
          </div>
        ) : (
          objectives.map(obj => (
            <div key={obj.id} className="card objective-item">
              {editingId === obj.id ? (
                <div>
                  <div className="form-group">
                    <input
                      type="text"
                      value={editContent}
                      onChange={(e) => setEditContent(e.target.value)}
                      autoFocus
                    />
                  </div>
                  <div className="form-group">
                    <input
                      type="text"
                      value={editCategory}
                      onChange={(e) => setEditCategory(e.target.value)}
                      placeholder="Category (optional)"
                    />
                  </div>
                  <div className="form-actions">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={handleCancelEdit}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => obj.id && handleSaveEdit(obj.id)}
                    >
                      Save
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="objective-content">
                    <p>{obj.content}</p>
                    {obj.category && (
                      <span className="objective-category">{obj.category}</span>
                    )}
                  </div>
                  <div className="objective-actions">
                    <button
                      className="btn btn-secondary"
                      onClick={() => handleStartEdit(obj)}
                    >
                      Edit
                    </button>
                    <button
                      className="btn btn-danger"
                      onClick={() => obj.id && onDeleteObjective(obj.id)}
                    >
                      Delete
                    </button>
                  </div>
                </>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Objectives;

